<template>
  <div>
    <h4 class="mb-1 font-weight-bold">
      {{ t('Returnable Product List') }}
    </h4>
    <h6 class="mb-2 text-info">
      {{ t('Refund Message') }}
    </h6>

    <div
      v-for="(packageItem, packageIndex) in refundablePackages"
      :key="packageIndex"
      class="consignment-package mb-3"
    >
      <h5 class="mb-1 font-weight-bolder">{{ packageIndex + 1 }}. Paket</h5>
      <table class="w-100 table table-bordered-layout table-sm">
        <thead>
          <tr>
            <th />
            <th>#</th>
            <th>
              {{ t('Origin') }}
            </th>
            <th>
              {{ t('Description') }}
            </th>
            <th>
              {{ t('Gtip Code') }}
            </th>
            <th>
              {{ t('Quantity') }}
            </th>
            <th>
              {{ t('Unit Price') }}
            </th>
          </tr>
        </thead>
        <tbody class="font-small-3">
          <template v-if="packageItem.items.some(x => parseInt(x.quantity) > 0)">
            <tr
              :key="index"
              v-for="(item, index) in packageItem.items.filter(i => parseInt(i.quantity) > 0)"
            >
              <td>
                <b-form-checkbox v-model="item.selected" class="mr-0" />
              </td>
              <td class="font-weight-bolder">
                {{ index + 1 }}
              </td>
              <td>
                <b-badge variant="dark">
                  {{ item.origin }}
                </b-badge>
              </td>
              <td class="font-weight-bolder">
                {{ item.description }}
              </td>
              <td>
                {{ item.gtip }}
              </td>
              <td>
                {{ item.quantity }}
              </td>
              <td>{{ item.unit_price }} ({{ item.currency }})</td>
            </tr>
            <tr
              :key="index + '_selected'"
              v-for="(item, index) in packageItem.items.filter(i => i.selected)"
            >
              <td colspan="7" class="pt-1">
                <b-form-group label-for="refund_quantity" :label="t('Quantity to be refund')">
                  <b-form-input
                    id="refund_quantity"
                    v-model="item.refund_quantity"
                    name="refund_quantity"
                    :placeholder="t('Quantity to be refund')"
                    :tabindex="6"
                    autocomplete="off"
                    :max="item.quantity"
                    type="number"
                    @input="handleRefundInput($event, item)"
                    @blur="handleRefundBlur($event, item)"
                  />
                </b-form-group>
              </td>
            </tr>
          </template>
          <template v-else>
            <tr>
              <td colspan="7" class="text-center text-info pt-2">
                {{ t('No returnable products in package') }}
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>
    <b-button
      size="sm"
      variant="primary"
      class="btn-tour-skip mr-1 mb-1"
      @click="setRefundProducts"
    >
      <span class="mr-25 align-middle">{{ t('Add to Refund List') }}</span>
      <feather-icon icon="XIcon" size="12" />
    </b-button>

    <h4 class="mb-1 font-weight-bold mt-3">
      {{ t('Products to be Returned') }}
    </h4>

    <template v-if="lastPackages && lastPackages.length > 0">
      <div
        :key="packageIndex + '_package'"
        v-for="(packageItem, packageIndex) in lastPackages"
        class="consignment-package mb-3"
      >
        <h5 class="mb-1 font-weight-bolder">{{ packageIndex + 1 }}. Paket</h5>
        <table class="w-100 table table-bordered-layout table-sm">
          <thead>
            <tr>
              <th>#</th>
              <th>
                {{ t('Origin') }}
              </th>
              <th>
                {{ t('Description') }}
              </th>
              <th>
                {{ t('Gtip Code') }}
              </th>
              <th>
                {{ t('Quantity') }}
              </th>
              <th>
                {{ t('Unit Price') }}
              </th>
              <th>
                {{ t('Actions') }}
              </th>
            </tr>
          </thead>
          <tbody class="font-small-3">
            <tr :key="index + 'packageItem'" v-for="(item, index) in packageItem.items">
              <td class="font-weight-bolder">
                {{ index + 1 }}
              </td>
              <td>
                <b-badge variant="dark">
                  {{ item.origin }}
                </b-badge>
              </td>
              <td class="font-weight-bolder">
                {{ item.description }}
              </td>
              <td>
                {{ item.gtip }}
              </td>
              <td>
                {{ item.quantity }}
              </td>
              <td>{{ item.unit_price }} ({{ item.currency }})</td>
              <td>
                <div
                  class="refund-product-delete-field text-danger"
                  @click="deleteItemFromLastPackages(packageItem.id, item.id)"
                >
                  <feather-icon icon="XIcon" size="18" />
                  {{ t('Delete') }}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
    <template v-else>
      <span class="text-danger">
        {{ t('No product to return was found.') }}
      </span>
    </template>
  </div>
</template>

<script>
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import { helper } from '@core/mixins/helper.js';
import { BBadge, BButton, BFormCheckbox, BFormGroup, BFormInput } from 'bootstrap-vue';

export default {
  components: {
    BButton,
    BFormInput,
    BFormGroup,
    BBadge,
    BFormCheckbox
  },
  mixins: [helper],
  props: {
    data: {
      type: Array,
      default() {
        return [];
      }
    }
  },

  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  },

  data() {
    return {
      refundablePackages: [],
      lastPackages: []
    };
  },

  mounted() {
    if (this.data && this.data.length) {
      this.refundablePackages = this.data.map(x => ({
        ...x,
        items: x.items.map(item => ({
          ...item,
          selected: false,
          refund_quantity: item.quantity
        }))
      }));
    }
  },

  methods: {
    setRefundProducts() {
      const selectedPackages = this.refundablePackages
        .filter(p => {
          const selectedItems = p.items.filter(item => item.selected);
          return selectedItems.length > 0;
        })
        .map(p => ({
          ...p,
          items: p.items.filter(item => item.selected)
        }));

      // Seçili paketlerin ve öğelerin işlemleri
      selectedPackages.forEach(packageItem => {
        const existingPackageIndex = this.lastPackages.findIndex(p => p.id === packageItem.id);
        if (existingPackageIndex !== -1) {
          const existingPackage = this.lastPackages[existingPackageIndex];
          // Package içindeki her bir item için işlem yap
          packageItem.items.forEach(item => {
            // Eğer item, existingPackage içinde bulunmuyorsa ekleyelim
            const existingItem = existingPackage.items.find(i => i.id === item.id);
            if (!existingItem) {
              existingPackage.items.push({
                ...item,
                quantity: parseInt(item.refund_quantity)
              });
            } else {
              // Eğer item existingPackage içinde bulunuyorsa, quantity değerini güncelleyelim
              existingItem.quantity += parseInt(item.refund_quantity);
            }
          });
        } else {
          const newPackage = {
            ...packageItem,
            items: packageItem.items.map(item => ({
              ...item,
              quantity: parseInt(item.refund_quantity)
            }))
          };
          this.lastPackages.push(newPackage);
          this.$emit('returnPackages', this.lastPackages);
        }
      });

      // refundablePackages'teki tüm paketleri ve öğeleri seçilmemiş olarak işaretle
      this.refundablePackages.forEach(p => {
        p.items.forEach(item => {
          item.selected = false;

          const matchingItem = selectedPackages
            .flatMap(packageItem => packageItem.items)
            .find(selectedItem => selectedItem.id === item.id);
          if (matchingItem) {
            item.quantity = parseInt(item.quantity) - parseInt(matchingItem.refund_quantity);
          }

          item.refund_quantity = item.quantity;
        });
      });
    },

    handleRefundInput(e, item) {
      const maxQuantity = parseInt(item.quantity);
      setTimeout(() => {
        if (e && parseInt(e) > maxQuantity) {
          item.refund_quantity = maxQuantity;
        } else if (e && parseInt(e) < 1) {
          item.refund_quantity = 1;
        }
      }, 100);
    },

    handleRefundBlur(e, item) {
      const { value } = e.target;
      setTimeout(() => {
        if (!value) {
          item.refund_quantity = item.quantity;
        }
      }, 100);
    },

    deleteItemFromLastPackages(packageId, itemId) {
      const packageIndex = this.lastPackages.findIndex(p => p.id === packageId);
      if (packageIndex !== -1) {
        const packageItem = this.lastPackages[packageIndex];

        const itemIndex = packageItem.items.findIndex(item => item.id === itemId);
        if (itemIndex !== -1) {
          const deletedItem = packageItem.items.splice(itemIndex, 1)[0];
          if (packageItem.items.length === 0) {
            this.lastPackages.splice(packageIndex, 1);
          }
          const refundablePackageIndex = this.refundablePackages.findIndex(p => p.id === packageId);
          if (refundablePackageIndex !== -1) {
            const refundablePackage = this.refundablePackages[refundablePackageIndex];
            const refundableItem = refundablePackage.items.find(item => item.id === itemId);
            if (refundableItem) {
              refundableItem.quantity += deletedItem.quantity;
              refundableItem.refund_quantity = refundableItem.quantity;
            }
          }
        }
      }

      this.$emit('returnPackages', this.lastPackages);
    },

    deleteItem(item) {
      item.deletionRequested = true;
    }
  }
};
</script>

<style>
.consignment-package {
  padding: 15px;
  border: 1px solid #e6e6e6;
  border-radius: 5px;
}
body.dark-layout .consignment-package {
  border-color: #3f4a6b;
}
</style>
